import i18n from 'i18next';
import isEqual from 'lodash/isEqual';
import { action, comparer, computed, makeObservable, observable, reaction } from 'mobx';

import { getUserLanguage } from '../../i18n/helpers';
import { getRequest, postRequest } from '../../services/api';
import { ldClient, resolveDynamicFlag, resolveFlag } from '../../services/launchDarkly';
import type { ApiResponse } from '../../services/types';
import { devError } from '../../utils/log';
import type { CoinType } from '../cointype/CoinTypeStore';
import type {
  FeatureFetchStatus,
  FeatureFetchType,
  FeatureManagerDialogType,
  FeatureStartTrigger,
  IFeatureManagerStore,
} from '../feature-manager/types';
import type { GameLabel } from '../game/types';
import type { BundleItem } from '../rewards/types';
import type { RootStore } from '../RootStore';
import { EnumOpenDialog } from '../ui/types';

import { selectCountdownTarget } from './battleCountdown';
import { canDrawBattleRive } from './battleRive';
import { isPlayerInBattleSegment } from './battleSegmentGate';
import { isBattleForLobby, selectLobbyBattle } from './battleWorld';
import { closingSeenKey, markClosingSeen, readSeenClosings } from './closingSeen';
import {
  MAX_TIMEOUT_MS,
  SLOT_BATTLE_CLAIM_PATH,
  SLOT_BATTLE_CLAIM_STATUSES,
  SLOT_BATTLE_CURRENT_PATH,
  SLOT_BATTLE_FETCH_COALESCE_MS,
  SLOT_BATTLE_FLAG,
  SLOT_BATTLE_LEADERBOARD_PATH,
  SLOT_BATTLE_LIVE_POLL_MS,
  SLOT_BATTLE_RESULTS_POLL_MS,
  SLOT_BATTLE_RESULTS_WINDOW_MS,
  SLOT_BATTLE_SETTLING_POLL_MS,
  SLOT_BATTLE_START_RETRY_LIMIT,
  SLOT_BATTLE_START_RETRY_POLL_MS,
} from './constants';
import { parseLeaderboardPage } from './leaderboardRows';
import { markOpeningSeen, openingSeenKey, readSeenOpenings } from './openingSeen';
import { selectBattlePanes } from './selectBattlePanes';
import { closingRewardProps, slotBattleId, trackSlotBattle } from './slotBattleAnalytics';
import type { BattleClaimStatus, BattleLeaderboardPage, SlotBattle } from './types';

/**
 * A 204 (this player matches no segment) arrives as an empty body. player-backend's
 * GetPlayerCurrentBattleService answers `{ data: { success, message, battle } }`, and the login read
 * carries each lobby's battle bare. Anything that is not recognisably a battle is no battle — `runKey`
 * and `segmentKey` are the two fields every state carries.
 */
export function parseSlotBattle(response: unknown): SlotBattle | null {
  const body = (response as { data?: { battle?: unknown } } | undefined)?.data;
  const candidate = (body ? body.battle : response) as Partial<SlotBattle> | null | undefined;

  if (!candidate || typeof candidate.runKey !== 'string' || typeof candidate.segmentKey !== 'string') {
    return null;
  }

  return {
    ...candidate,
    // core's enums are upper case (`PERCENTAGE`); the client's types are not all.
    coinType: typeof candidate.coinType === 'string' ? candidate.coinType.toUpperCase() : candidate.coinType,
    progressDisplay:
      typeof candidate.progressDisplay === 'string'
        ? candidate.progressDisplay.toLowerCase()
        : candidate.progressDisplay,
    contenders: Array.isArray(candidate.contenders) ? candidate.contenders : [],
    closing: candidate.closing ?? null,
  } as SlotBattle;
}

/**
 * player-backend's login read for both lobbies answers `{ data: { success, message, battles: { gc, sc } } }`,
 * or an empty 204 when the player is in no battle in either. Null when the answer is neither: a failed read.
 */
export function parseSlotBattlesByCoin(response: unknown): Record<CoinType, SlotBattle | null> | null {
  if (response === '' || response === null || response === undefined) {
    return { GC: null, SC: null };
  }

  const battles = (response as { data?: { battles?: unknown } })?.data?.battles;

  if (battles === null) {
    return { GC: null, SC: null };
  }

  if (!battles || typeof battles !== 'object') {
    return null;
  }

  const { gc, sc } = battles as { gc?: unknown; sc?: unknown };
  return { GC: parseSlotBattle(gc ?? null), SC: parseSlotBattle(sc ?? null) };
}

/**
 * player-backend's ClaimBattleRewardService answers `{ data: { success, message, status } }`. A
 * status outside the contract is not a claim outcome the pane can act on, so it reads as null.
 */
export function parseClaimStatus(response: unknown): BattleClaimStatus | null {
  const status = (response as { data?: { status?: unknown } } | undefined)?.data?.status;

  return (SLOT_BATTLE_CLAIM_STATUSES as readonly unknown[]).includes(status) ? (status as BattleClaimStatus) : null;
}

/**
 * Whether the battle is still in `state` although the date that ends it has passed: `upcoming` after
 * its start, or `live` after its end. core's cache or a client clock running ahead of the server's
 * both produce this, and the next read moves it on.
 */
function isPastOwnBoundary(battle: SlotBattle | null, state: 'upcoming' | 'live', nowMs: number): boolean {
  const iso = state === 'upcoming' ? battle?.startDate : battle?.endDate;

  if (battle?.state !== state || !iso) {
    return false;
  }

  const boundaryMs = new Date(iso).getTime();
  return !Number.isNaN(boundaryMs) && boundaryMs <= nowMs;
}

// Local aliases: decorated members cannot name an `import type` directly under emitDecoratorMetadata.
type LobbyCoin = CoinType;
type LobbyBattle = SlotBattle;
type LobbyBattles = Partial<Record<CoinType, SlotBattle | null>>;

/** How the dialog was opened, for `uiStore.openDialog.options.shown_by`. */
type SlotBattleShownBy = 'widget' | 'log_in' | 'back_to_lobby';

/**
 * Battle of the Slots (GS-813) — the player's current battle segment for the active coin type.
 *
 * Modelled on `TournamentStore`: one coin-scoped read, coalesced and de-duplicated per coin, with a
 * failed request deliberately leaving whatever is already rendered alone. Registered with
 * `FeatureManagerStore`, which refetches it with the lobby and auto-opens the win popup (the closing
 * pane) once per settled result — both only while `/v2/ui/features/config` has a `SlotBattle` entry.
 * Every other pane opens from the widget only.
 */
export class SlotBattleStore implements IFeatureManagerStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    this.slotBattleFlag = resolveFlag(SLOT_BATTLE_FLAG, false);

    ldClient?.on('ready', () => {
      this.setSlotBattleFlag(ldClient?.variation(SLOT_BATTLE_FLAG, false) ?? false);
    });

    ldClient?.on('change', (changes) => {
      if (SLOT_BATTLE_FLAG in changes) {
        this.setSlotBattleFlag(Boolean(changes[SLOT_BATTLE_FLAG].current));
      }
    });

    // Auth and the kill switch start a new session: everything read so far is dropped, and the login
    // read fills both lobbies at once.
    reaction(
      () => ({
        enabled: this.slotBattleFlag,
        authorized: this.rootStore.authStore.authorized,
      }),
      ({ enabled, authorized }) => {
        this.invalidate();

        if (enabled && authorized) {
          void this.fetchBothLobbies();
        }
      },
      {
        // The data function builds a fresh object every run, so reference equality would re-fire on
        // every unrelated tick.
        equals: isEqual,
        // A returning player can already be authorized by the time this store is constructed.
        fireImmediately: true,
      },
    );

    // A coin switch keeps the session: the new lobby's battle shows at once from what was read, and its
    // own read runs behind it (GS-1005). With nothing read for it yet, the battle on screen stays meanwhile.
    reaction(
      () => this.rootStore.coinTypeStore.coinType,
      (coinType) => this.switchLobby(coinType),
    );

    // core picks the run's images (the opening/win `.riv`, the widget art, the badge) by the app
    // language the request carries, so a language switch re-reads now instead of on the next reload.
    // The battle on screen stays until the answer lands, as it does on any other refetch.
    i18n.on('languageChanged', (rawLang) => {
      const lang = getUserLanguage(rawLang);

      if (this.prevLang === lang) {
        return;
      }

      this.prevLang = lang;

      // Skipped, not deferred, on a closing or a claim: the pane's own forced read after the claim (or
      // the next dialog open) goes out with the new language anyway.
      if (this.mayReadUnprompted) {
        this.refetchAfterInflight();
      }
    });

    reaction(
      () => this.pollIntervalMs,
      (intervalMs) => this.schedulePoll(intervalMs),
      { fireImmediately: true },
    );

    reaction(
      () => this.nextBoundaryMs,
      (boundaryMs) => this.scheduleBoundary(boundaryMs),
      { fireImmediately: true },
    );
  }

  public readonly dialogTypes: FeatureManagerDialogType[] = ['SlotBattle'];

  /** The app language last seen, so a repeated `languageChanged` for it reads nothing. */
  private prevLang = getUserLanguage();

  /** Global kill switch. Off means every surface disappears and nothing is fetched. */
  @observable
  public slotBattleFlag: boolean = false;

  /** The battle of the lobby on screen. */
  @observable
  public currentBattle: SlotBattle | null = null;

  /** The last answer for each lobby this session; a lobby not read yet has no entry. */
  @observable
  private battleByCoinType: LobbyBattles = {};

  @observable
  public loading: boolean = false;

  /** A claim is in flight. The closing pane's CTA stays disabled until it settles. */
  @observable
  public claimPending: boolean = false;

  @observable
  private seenOpenings: string[] = readSeenOpenings();

  @observable
  private seenClosings: string[] = readSeenClosings();

  /**
   * Battle Rive files (`optInImageUrl`) that failed to load or did not match the contract. Session-only
   * on purpose: a broken file skips the opening and sends the win popup to its fallback for now, and a
   * fixed one shows on the next visit.
   */
  @observable
  private failedRiveUrls: string[] = [];

  /**
   * Openings already put on screen this session, by `openingSeenKey`: auto-opened, or opened from the
   * widget. Its CTA or the backdrop marks it seen for good (see `SlotBattleDialog`); this covers a dialog
   * closed any other way (another dialog taking the slot, a logout), so the feature manager never offers
   * the same opening twice in a session.
   */
  @observable
  private offeredOpenings: string[] = [];

  /**
   * Whatever the lobby's own read returned, held to the lobby's world (GS-1032): a live battle only in its
   * own coin's lobby, a closing in either (`isBattleForLobby`).
   */
  @computed
  public get battle(): SlotBattle | null {
    return this.slotBattleFlag ? selectLobbyBattle(this.currentBattle, this.rootStore.coinTypeStore.coinType) : null;
  }

  /** The per-run LaunchDarkly flag key, or null. Read through `useDynamicFlag`, never `useFlag`. */
  @computed
  public get featureFlagKey(): string | null {
    return this.battle?.featureFlagKey ?? null;
  }

  @computed
  public get canShowWidget(): boolean {
    const battle = this.battle;

    if (!battle) {
      return false;
    }

    // No artwork means both rails render nothing, so it must not take a rail slot or count toward layout.
    if (!battle.widgetImageUrl) {
      return false;
    }

    // A NEXT TIME closing this client cannot draw has nothing to open either.
    if (this.isClosingHidden) {
      return false;
    }

    // Settled with no closing payload is a finished run past its claim window: nothing left to open.
    return battle.state !== 'settled' || Boolean(battle.closing);
  }

  /**
   * The lobby badge while the battle is live: core's `slot_battle` label and every game that scores for
   * it (a slot's own game and the fallback it is played through). Only in the run's own coin, as the
   * battle itself (GS-1032). Compared structurally, so a refresh with the same contenders does not
   * re-render every game tile.
   */
  @computed({ equals: comparer.structural })
  public get liveBattleBadge(): {
    label: GameLabel;
    gameIds: number[];
    featureFlagKey: string;
    segmentKey: string;
  } | null {
    const battle = this.battle;

    if (!battle?.gameLabel || battle.state !== 'live') {
      return null;
    }

    const gameIds = battle.contenders.flatMap((contender) =>
      contender.playGameId === undefined || contender.playGameId === contender.gameId
        ? [contender.gameId]
        : [contender.gameId, contender.playGameId],
    );

    return {
      label: battle.gameLabel,
      gameIds,
      featureFlagKey: battle.featureFlagKey,
      segmentKey: battle.segmentKey,
    };
  }

  private get openingKey(): string | null {
    const battle = this.battle;

    return battle
      ? openingSeenKey(this.rootStore.userStore.profile?.userId ?? '', battle.runKey, battle.segmentKey)
      : null;
  }

  private get closingKey(): string | null {
    const battle = this.battle;

    return battle?.closing
      ? closingSeenKey(this.rootStore.userStore.profile?.userId ?? '', battle.runKey, battle.segmentKey)
      : null;
  }

  /**
   * A settled result waiting to be claimed that this player has not been shown yet (see `closingSeen`).
   * The win popup opens by itself for it, once; after that only the widget opens it.
   */
  @computed
  public get hasUnseenClosing(): boolean {
    const key = this.closingKey;

    return key !== null && !this.isClosingHidden && !this.seenClosings.includes(key);
  }

  /**
   * A live battle whose opening this player has neither dismissed nor been shown this session.
   * The feature manager opens it by itself, like a metagame's intro. Same gates as the dialog: the
   * opening must be its first pane (`.riv`, not seen, not failed, not counting), and the run flag must
   * place the player in this segment (`useSlotBattle`), so the popup never shows a battle the widget hides.
   * Not `@computed`: the run flag is read from the LaunchDarkly client, which MobX cannot track.
   */
  public get hasUnseenOpening(): boolean {
    const battle = this.battle;
    const key = this.openingKey;

    if (!battle || key === null || this.offeredOpenings.includes(key)) {
      return false;
    }

    const panes = selectBattlePanes(battle, this.isOpeningSeen, this.isRiveFailed, this.isCountingResults);

    return (
      panes[0] === 'opening' &&
      isPlayerInBattleSegment(
        resolveDynamicFlag<string | undefined>(battle.featureFlagKey, undefined),
        battle.segmentKey,
      )
    );
  }

  /**
   * A NEXT TIME closing (a segment player the battle paid nothing) this client cannot draw. The design
   * has it only as the Rive screen, so with no `.riv`, no winner among the contenders, or a file that
   * failed or lacks the screen, there is no popup and no widget for it, and `acknowledgeHiddenClosing`
   * dismisses it instead.
   */
  @computed
  public get isClosingHidden(): boolean {
    const battle = this.battle;

    if (!battle?.closing || battle.closing.outcome !== 'lost_no_reward') {
      return false;
    }

    return this.isRiveFailed || !canDrawBattleRive(battle, 'next_time');
  }

  /**
   * Between the battle's end and its closing: the dialog shows that the results are being counted
   * rather than a frozen podium. From core's `settling`, or from the end as this client's clock sees
   * it, whichever comes first.
   */
  @computed
  public get isCountingResults(): boolean {
    const battle = this.battle;

    return Boolean(battle && !battle.closing && (battle.state === 'settling' || this.isLivePastEnd));
  }

  /** Whether this player already dismissed the current battle's opening pane (see `openingSeen`). */
  @computed
  public get isOpeningSeen(): boolean {
    const key = this.openingKey;

    return key !== null && this.seenOpenings.includes(key);
  }

  /** Whether the current battle's Rive file already failed this session (see `failedRiveUrls`). */
  @computed
  public get isRiveFailed(): boolean {
    const url = this.battle?.optInImageUrl;

    return Boolean(url) && this.failedRiveUrls.includes(url as string);
  }

  @computed
  public get isDialogOpen(): boolean {
    return this.rootStore.uiStore.openDialog.dialog === EnumOpenDialog.isOpenSlotBattleDialog;
  }

  /**
   * The clock as of the last read or passed boundary. Observable, so the computeds that compare a
   * battle's dates against it re-run exactly when one of those dates passes, and at no other tick.
   */
  @observable
  private clockMs: number = Date.now();

  @action
  private tickClock() {
    this.clockMs = Date.now();
  }

  /**
   * When the battle next changes state by itself: its start while upcoming, its end while live. The
   * store re-reads at that moment whatever the lobby shows, since neither rail can be counted on: the
   * legacy icon has no timer, and the redesign widget only counts down while it is mounted.
   */
  @computed
  private get nextBoundaryMs(): number | null {
    const battle = this.battle;

    if (!battle || !this.rootStore.authStore.authorized) {
      return null;
    }

    return selectCountdownTarget(battle, this.clockMs)?.getTime() ?? null;
  }

  /** core answers `settling` from the end on, but a client clock running ahead of the server's still reads `live`. */
  @computed
  private get isLivePastEnd(): boolean {
    return isPastOwnBoundary(this.currentBattle, 'live', this.clockMs);
  }

  /** The read at the start can be core's cached pre-start page, or come from a client clock running ahead. */
  @computed
  private get isUpcomingPastStart(): boolean {
    return isPastOwnBoundary(this.currentBattle, 'upcoming', this.clockMs);
  }

  /**
   * From the end, until the closing arrives or `SLOT_BATTLE_RESULTS_WINDOW_MS` has passed: the stretch
   * in which settlement is expected (core settles at end + its buffer, about 20 s), read fast.
   */
  @computed
  private get isAwaitingResults(): boolean {
    const battle = this.currentBattle;

    if (!battle || battle.closing || (battle.state !== 'live' && battle.state !== 'settling')) {
      return false;
    }

    const endMs = new Date(battle.endDate).getTime();

    return !Number.isNaN(endMs) && this.clockMs >= endMs && this.clockMs < endMs + SLOT_BATTLE_RESULTS_WINDOW_MS;
  }

  /** Answers in a row that still said `upcoming` after the start. Any other answer resets it. */
  @observable
  private upcomingPastStartReads: number = 0;

  /**
   * Whether the store may read on its own — the poll, a language switch — rather than on a caller's
   * request. Never for a guest (an authenticated endpoint: a guest's read can only 401, and the auth
   * reaction reads on login anyway) or with the kill switch off. Never while a closing is on screen or
   * a claim is in flight either: the closing pane changes only through the claim, which refetches after
   * its reward animation, and a read landing mid-animation would close the dialog under it.
   */
  @computed
  private get mayReadUnprompted(): boolean {
    return (
      this.slotBattleFlag && this.rootStore.authStore.authorized && !this.currentBattle?.closing && !this.claimPending
    );
  }

  /**
   * How often to re-read on our own, or null for never. Fast from the end until the closing arrives,
   * dialog or not, for at most `SLOT_BATTLE_RESULTS_WINDOW_MS`, so the win popup opens within seconds
   * of settlement. Live while the dialog is open; slower while the run settles past that window,
   * dialog or not, so the closing pane still shows up without a reload — and at that pace too for a
   * live battle whose end has passed, until the server answers `settling`. An upcoming
   * battle whose start has passed is re-read quickly a few times, then at the settling pace, until
   * the server answers `live`. The closing pane is not polled (see `mayReadUnprompted`).
   */
  @computed
  private get pollIntervalMs(): number | null {
    if (!this.mayReadUnprompted) {
      return null;
    }

    if (this.isAwaitingResults) {
      return SLOT_BATTLE_RESULTS_POLL_MS;
    }

    if (this.isDialogOpen) {
      return SLOT_BATTLE_LIVE_POLL_MS;
    }

    if (this.isUpcomingPastStart) {
      return this.upcomingPastStartReads <= SLOT_BATTLE_START_RETRY_LIMIT
        ? SLOT_BATTLE_START_RETRY_POLL_MS
        : SLOT_BATTLE_SETTLING_POLL_MS;
    }

    return this.currentBattle?.state === 'settling' || this.isLivePastEnd ? SLOT_BATTLE_SETTLING_POLL_MS : null;
  }

  private boundaryTimer: ReturnType<typeof setTimeout> | null = null;

  private scheduleBoundary(boundaryMs: number | null) {
    if (this.boundaryTimer) {
      clearTimeout(this.boundaryTimer);
      this.boundaryTimer = null;
    }

    if (boundaryMs === null) {
      return;
    }

    const delayMs = Math.min(Math.max(boundaryMs - Date.now(), 0), MAX_TIMEOUT_MS);

    this.boundaryTimer = setTimeout(() => {
      this.boundaryTimer = null;

      // A boundary further off than setTimeout can wait: wait again.
      if (Date.now() < boundaryMs) {
        this.scheduleBoundary(boundaryMs);
        return;
      }

      this.tickClock();
      void this.fetch({ force: true });
    }, delayMs);
  }

  private pollTimer: ReturnType<typeof setInterval> | null = null;

  private schedulePoll(intervalMs: number | null) {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }

    if (intervalMs === null) {
      return;
    }

    this.pollTimer = setInterval(() => {
      // Moves the clock even when the read fails or is skipped, so the results window still closes.
      this.tickClock();

      // A background tab reads nothing; the next visible tick catches up.
      if (!document.hidden) {
        void this.fetch({ force: true });
      }
    }, intervalMs);
  }

  @action
  private setSlotBattleFlag(value: boolean) {
    this.slotBattleFlag = value;
  }

  @action
  public setCurrentBattle(battle: SlotBattle | null) {
    this.currentBattle = battle;
    this.clockMs = Date.now();
    this.upcomingPastStartReads = isPastOwnBoundary(battle, 'upcoming', this.clockMs)
      ? this.upcomingPastStartReads + 1
      : 0;
  }

  @action
  private setLoading(loading: boolean) {
    this.loading = loading;
  }

  @action
  private setClaimPending(pending: boolean) {
    this.claimPending = pending;
  }

  @action
  public reset() {
    this.invalidate();
  }

  @action
  public dismissOpening() {
    const key = this.openingKey;

    if (key !== null) {
      this.seenOpenings = markOpeningSeen(key);
    }
  }

  @action
  private markOpeningOffered() {
    const key = this.openingKey;

    if (key !== null && !this.offeredOpenings.includes(key)) {
      this.offeredOpenings.push(key);
    }
  }

  @action
  private markClosingShown() {
    const key = this.closingKey;

    if (key !== null) {
      this.seenClosings = markClosingSeen(key);
    }
  }

  @action
  public markRiveFailed() {
    const url = this.battle?.optInImageUrl;

    if (url && !this.failedRiveUrls.includes(url)) {
      this.failedRiveUrls.push(url);
    }

    this.acknowledgeHiddenClosing();
  }

  /** Hidden closings already acknowledged this session, so a read that still carries one posts nothing. */
  private quietAcks = new Set<string>();

  /**
   * Dismisses a hidden NEXT TIME closing with the claim its CTA would have posted. Core serves an
   * unacknowledged closing ahead of a live battle, so leaving it would hide the next battle for the whole
   * claim window. Once per closing per session; a post that fails or is not sent is retried on the next read.
   */
  private acknowledgeHiddenClosing() {
    const key = this.closingKey;

    if (!this.isClosingHidden || key === null || this.quietAcks.has(key)) {
      return;
    }

    this.quietAcks.add(key);
    this.claimClosing()
      .then((status) => {
        if (status === null) {
          this.quietAcks.delete(key);
          return;
        }

        // After any read still in flight, which a forced read would join: it predates the acknowledgement.
        this.refetchAfterInflight();
      })
      .catch((error: unknown) => {
        this.quietAcks.delete(key);
        devError('SlotBattleStore.acknowledgeHiddenClosing', error);
      });
  }

  /**
   * A forced read once any read still in flight has settled. A forced read joins one in flight, and that
   * one predates whatever this read is for (an acknowledgement, or a language switch whose header it
   * was sent without).
   */
  private refetchAfterInflight() {
    const inflight = this.inflightByCoinType[this.rootStore.coinTypeStore.coinType];

    void Promise.resolve(inflight).finally(() => {
      void this.fetch({ force: true });
    });
  }

  /**
   * Every auth, kill-switch or coin transition starts a new generation: in-flight reads from the old
   * one can no longer write, and the new session never joins their promises.
   */
  @action
  private invalidate() {
    this.generation += 1;
    this.currentBattle = null;
    this.battleByCoinType = {};
    this.appliedReadByCoinType = {};
    this.upcomingPastStartReads = 0;
    this.loading = false;
    this.inflightByCoinType = {};
    this.lastResultByCoinType = {};
  }

  /**
   * With nothing read for the new lobby yet, the battle on screen stays only if it may show there (a
   * closing); another world's live battle goes at once (GS-1032).
   */
  @action
  private switchLobby(coinType: LobbyCoin) {
    if (coinType in this.battleByCoinType) {
      this.setCurrentBattle(this.battleByCoinType[coinType] ?? null);
    } else if (!isBattleForLobby(this.currentBattle, coinType)) {
      this.setCurrentBattle(null);
    }

    if (this.slotBattleFlag && this.rootStore.authStore.authorized) {
      void this.fetch({ force: true });
    }
  }

  /**
   * Stores a lobby's answer, and shows it if that lobby is still on screen. An answer older than one
   * already stored for the lobby is dropped: a login read can land after the switch's own read.
   */
  @action
  private storeLobbyBattle(coinType: LobbyCoin, answer: LobbyBattle | null, readId: number) {
    if ((this.appliedReadByCoinType[coinType] ?? 0) > readId) {
      return;
    }

    const battle = selectLobbyBattle(answer, coinType);

    this.trackLeaderboardUnlock(this.battleByCoinType[coinType] ?? null, battle);
    this.appliedReadByCoinType[coinType] = readId;
    this.battleByCoinType[coinType] = battle;

    if (this.rootStore.coinTypeStore.coinType === coinType) {
      this.setCurrentBattle(battle);
    }
  }

  /** Opening it on a closing counts as the player having seen that result, however it was opened. */
  public openDialog(triggeredBy: SlotBattleShownBy = 'widget') {
    this.markClosingShown();
    this.markOpeningOffered();
    this.rootStore.uiStore.setOpenDialog({
      dialog: EnumOpenDialog.isOpenSlotBattleDialog,
      options: { shown_by: triggeredBy },
    });
    void this.fetch({ force: true });
  }

  /**
   * The feature manager's turn for `SlotBattle`: opens the win popup for a result this player has not
   * been shown yet, or else the opening of a live battle (see `hasUnseenOpening`). The standings and
   * leaderboard never auto-open — they are widget-only.
   */
  public startDialogs(_type?: FeatureManagerDialogType, trigger?: FeatureStartTrigger): boolean {
    if ((!this.hasUnseenClosing && !this.hasUnseenOpening) || this.isDialogOpen) {
      return false;
    }

    this.openDialog(trigger === 'after_login' ? 'log_in' : 'back_to_lobby');

    // Refused (a blocked dialog): report it, or the sequence waits on a popup that never opened.
    if (!this.isDialogOpen) {
      return false;
    }

    this.finishTurnOnClose();
    return true;
  }

  /**
   * Hands the feature manager its turn back however this dialog leaves the slot. `closeDialog` already
   * does for the dialog's own exits; another dialog taking the slot, or a logout, goes around it, and
   * the sequence would otherwise wait in FEATURE_IN_PROGRESS for the rest of the session.
   */
  private finishTurnOnClose() {
    const dispose = reaction(
      () => this.isDialogOpen,
      (isOpen) => {
        if (!isOpen) {
          dispose();
          this.featureManager?.onDialogFinished('SlotBattle');
        }
      },
    );
  }

  public closeDialog() {
    if (this.isDialogOpen) {
      this.rootStore.uiStore.setCloseDialog();
      // Lets the feature manager move on to the next queued popup, when it opened this one.
      this.featureManager?.onDialogFinished('SlotBattle');
    }
  }

  /**
   * After CLAIM: opens the free spins the claim granted right after the popup, as a claimed gift does
   * (see `EventTriggerStore.handOverToGrantedFreeSpins`), instead of at their own lobby slot. On the
   * feature manager's SlotBattle turn they go next and the queue resumes after them; opened from the
   * widget there is no turn to hand over, so they open straight away. Closes the popup either way.
   */
  public async closeAfterClaim(bundleItems: BundleItem[]): Promise<void> {
    const { userFreeSpinsStore } = this.rootStore;
    const queued = await userFreeSpinsStore.queueGrantedDialog(bundleItems);
    const featureManager = this.featureManager;
    const handedOver =
      queued &&
      featureManager?.getCurrentFeatureType() === 'SlotBattle' &&
      featureManager.showNextThenResume('UserFreeSpins');

    this.closeDialog();

    if (queued && !handedOver) {
      userFreeSpinsStore.startDialogs('UserFreeSpins');
    }
  }

  /** Constructed after this store in `RootStore`, and absent from the narrower roots tests build. */
  private get featureManager(): RootStore['featureManagerStore'] | undefined {
    return this.rootStore.featureManagerStore as RootStore['featureManagerStore'] | undefined;
  }

  /**
   * A closing or an opening that lands on one of this store's own reads (the results poll, the start
   * or end boundary) would otherwise wait for the next lobby visit to auto-open. Queue it with the
   * feature manager, which opens it in its turn, once no other dialog is showing and the player is not in a game. Only with
   * a `SlotBattle` entry in the features config: without one the sequence never starts this store,
   * and a queued type with no status would never be started either.
   */
  private queueUnseenPopup() {
    const featureManager = this.featureManager;

    if (!this.hasUnseenClosing && !this.hasUnseenOpening) {
      return;
    }

    // The open dialog swaps to the new pane by itself: the player is looking at it now.
    if (this.isDialogOpen) {
      this.markClosingShown();
      this.markOpeningOffered();
      return;
    }

    if (!featureManager?.dialogManagerData?.SlotBattle) {
      return;
    }

    // The lobby read may have found nothing to open yet, leaving the status OFF; the sequence skips OFF types.
    featureManager.updateFeatureStatus('SlotBattle', 'READY');
    featureManager.addDialogsToSequenceQueue(['SlotBattle']);
  }

  /**
   * A closing shows in both lobbies (GS-1032 decision 16). Once answered, the other lobby's stored copy is
   * dropped, so switching there does not draw it again before that lobby's own read lands.
   */
  @action
  private forgetClosingElsewhere(battle: LobbyBattle) {
    const id = slotBattleId(battle);

    (Object.keys(this.battleByCoinType) as CoinType[]).forEach((coinType) => {
      const stored = this.battleByCoinType[coinType];

      if (coinType !== this.rootStore.coinTypeStore.coinType && stored?.closing && slotBattleId(stored) === id) {
        delete this.battleByCoinType[coinType];
      }
    });
  }

  /**
   * Claims the current battle's settled result — or, for a no-reward closing, acknowledges it; core
   * stamps both the same way. The slot-battle twin of `TournamentStore.finishTournamentForPlayer`.
   *
   * Resolves with core's status (only `claimed` means a credit happened) and throws on a failed
   * request or an unrecognised answer, so the pane can re-enable its CTA. It does not refetch: the
   * caller refetches once any reward animation has played, as the tournament finish flow does.
   */
  public async claimClosing(): Promise<BattleClaimStatus | null> {
    const battle = this.battle;

    if (!battle?.closing) {
      devError('SlotBattleStore.claimClosing', 'No settled battle to claim');
      return null;
    }

    if (this.claimPending) {
      return null;
    }

    this.setClaimPending(true);

    try {
      const response = await postRequest(
        `${SLOT_BATTLE_CLAIM_PATH}/${encodeURIComponent(battle.runKey)}/${encodeURIComponent(battle.segmentKey)}`,
      );
      const status = parseClaimStatus(response);

      if (!status) {
        throw new Error('Unrecognised slot battle claim response');
      }

      if (status === 'claimed') {
        trackSlotBattle('reward_granted', battle, {
          event_action: 'impression',
          event_type: 'battle_outcome',
          event_value: battle.closing.outcome,
          ...closingRewardProps(battle.closing),
        });
      }

      this.forgetClosingElsewhere(battle);

      return status;
    } finally {
      this.setClaimPending(false);
    }
  }

  /**
   * One leaderboard page past the first, for scrolling (contract §9.1). Never throws and never retries by
   * itself: null is a failed read (a core outage answers an error), which the caller may retry later.
   */
  public async fetchLeaderboardPage(
    battle: Pick<SlotBattle, 'runKey' | 'segmentKey'>,
    offset: number,
    limit: number,
  ): Promise<BattleLeaderboardPage | null> {
    try {
      const response = await getRequest<ApiResponse<unknown>>(
        `${SLOT_BATTLE_LEADERBOARD_PATH}/${encodeURIComponent(battle.runKey)}/${encodeURIComponent(battle.segmentKey)}`,
        { offset, limit },
        undefined,
        { skipAuthLogout: true },
      );

      return parseLeaderboardPage(response);
    } catch (error) {
      devError('SlotBattleStore.fetchLeaderboardPage', error);
      return null;
    }
  }

  /** Battles whose unlock this session already reported. */
  private reportedUnlocks = new Set<string>();

  /**
   * `leaderboard_unlocked`, once per battle: when a read of the same battle shows the lock bar filled
   * where the last one did not. A battle first read already unlocked is not an unlock the client saw.
   */
  private trackLeaderboardUnlock(previous: SlotBattle | null, next: SlotBattle | null) {
    // Optional chaining on purpose: this runs inside `storeLobbyBattle`, and a throw there would lose the read.
    if (
      !previous ||
      !next ||
      slotBattleId(previous) !== slotBattleId(next) ||
      previous.qualification?.unlocked !== false ||
      next.qualification?.unlocked !== true
    ) {
      return;
    }

    const id = slotBattleId(next);

    if (this.reportedUnlocks.has(id)) {
      return;
    }

    this.reportedUnlocks.add(id);
    trackSlotBattle('leaderboard_unlocked', next, {
      event_action: 'impression',
      progress: next.qualification.progress,
      threshold: next.qualification.threshold,
    });
  }

  private generation = 0;
  /** Every read takes the next id, so an older answer never overwrites a newer one for the same lobby. */
  private lastReadId = 0;
  private appliedReadByCoinType: Partial<Record<CoinType, number>> = {};
  private inflightByCoinType: Partial<Record<CoinType, Promise<FeatureFetchStatus>>> = {};
  private lastResultByCoinType: Partial<Record<CoinType, { at: number; status: FeatureFetchStatus }>> = {};

  /**
   * The feature manager calls this with `{ fetchType }`, everything else with `{ force }`. A forced
   * read skips the coalesce window but still joins one already in flight.
   */
  public fetch(
    request?: { fetchType?: FeatureFetchType; force?: boolean },
    options?: { force?: boolean },
  ): Promise<FeatureFetchStatus> {
    if (!this.slotBattleFlag) {
      return Promise.resolve('OFF');
    }

    const { coinType } = this.rootStore.coinTypeStore;

    const inflight = this.inflightByCoinType[coinType];
    if (inflight) {
      return inflight;
    }

    if (!request?.force && !options?.force) {
      const last = this.lastResultByCoinType[coinType];
      if (last && Date.now() - last.at < SLOT_BATTLE_FETCH_COALESCE_MS) {
        return Promise.resolve(last.status);
      }
    }

    const { generation } = this;
    // The feature manager starts this store's popup itself after a lobby read; every other read queues it.
    const queuePopup = !request?.fetchType;
    const promise: Promise<FeatureFetchStatus> = this.performFetch(coinType, generation, queuePopup)
      .then((status) => {
        if (generation === this.generation) {
          this.lastResultByCoinType[coinType] = { at: Date.now(), status };
        }
        return status;
      })
      .finally(() => {
        // A newer generation may already own this slot.
        if (this.inflightByCoinType[coinType] === promise) {
          delete this.inflightByCoinType[coinType];
        }
      });

    this.inflightByCoinType[coinType] = promise;
    return promise;
  }

  /**
   * The login read: both lobbies in one request, so a later coin switch shows the other lobby's battle at
   * once. Registered as the lobby's read in flight, so a read for it joins this one. A failed read, like
   * the single one's, is ERROR and writes nothing.
   */
  private fetchBothLobbies(): Promise<FeatureFetchStatus> {
    const { coinType } = this.rootStore.coinTypeStore;
    const { generation } = this;

    const promise: Promise<FeatureFetchStatus> = this.performBothLobbiesFetch(coinType, generation)
      .then((status) => {
        if (generation === this.generation) {
          this.lastResultByCoinType[coinType] = { at: Date.now(), status };
        }
        return status;
      })
      .finally(() => {
        if (this.inflightByCoinType[coinType] === promise) {
          delete this.inflightByCoinType[coinType];
        }
      });

    this.inflightByCoinType[coinType] = promise;
    return promise;
  }

  private async performBothLobbiesFetch(coinType: CoinType, generation: number): Promise<FeatureFetchStatus> {
    const readId = ++this.lastReadId;
    this.setLoading(true);

    try {
      const response = await getRequest<ApiResponse<unknown>>(
        `${SLOT_BATTLE_CURRENT_PATH}/${coinType.toLowerCase()}/by-coin`,
      );

      if (generation !== this.generation) {
        return 'OFF';
      }

      const battles = parseSlotBattlesByCoin(response);

      if (!battles) {
        return 'ERROR';
      }

      this.storeLobbyBattle('GC', battles.GC, readId);
      this.storeLobbyBattle('SC', battles.SC, readId);
      this.preloadWidgetImages(battles);
      this.acknowledgeHiddenClosing();
      this.queueUnseenPopup();

      return this.currentBattle ? 'READY' : 'OFF';
    } catch (error) {
      devError('SlotBattleStore.fetchBothLobbies', error);
      return generation === this.generation ? 'ERROR' : 'OFF';
    } finally {
      if (generation === this.generation) {
        this.setLoading(false);
      }
    }
  }

  /** So the other lobby's widget draws at once on a switch, not after its art downloads. */
  private preloadWidgetImages(battles: Record<CoinType, SlotBattle | null>) {
    for (const battle of Object.values(battles)) {
      if (battle?.widgetImageUrl && typeof Image !== 'undefined') {
        new Image().src = battle.widgetImageUrl;
      }
    }
  }

  private async performFetch(coinType: CoinType, generation: number, queuePopup: boolean): Promise<FeatureFetchStatus> {
    const readId = ++this.lastReadId;
    this.setLoading(true);

    try {
      const response = await getRequest<ApiResponse<SlotBattle>>(
        `${SLOT_BATTLE_CURRENT_PATH}/${coinType.toLowerCase()}`,
      );

      // The player logged out or switched account mid-request: this answer belongs to a session that is gone.
      if (generation !== this.generation) {
        return 'OFF';
      }

      const battle = parseSlotBattle(response);
      this.storeLobbyBattle(coinType, battle, readId);
      this.acknowledgeHiddenClosing();

      if (queuePopup) {
        this.queueUnseenPopup();
      }

      return battle ? 'READY' : 'OFF';
    } catch (error) {
      // Left as ERROR with nothing written: a 500 must not be indistinguishable from "no battle",
      // which is what would make a live run's widget and dialog vanish mid-window.
      devError('SlotBattleStore.fetch', error);
      return 'ERROR';
    } finally {
      if (generation === this.generation) {
        this.setLoading(false);
      }
    }
  }
}
